// ช่องกรอกวันที่แบบ พ.ศ. — ตัวแทนของ <Form.Control type="date" />
//
// input[type=date] ของเบราว์เซอร์แสดงปีเป็น ค.ศ. เสมอ และหน้าเว็บสั่งไม่ได้
// (รูปแบบมาจาก locale ของเครื่องผู้ใช้ ไม่ใช่จากโค้ด) ครูที่เห็น 2026 แล้วเข้าใจว่า
// เป็น พ.ศ. จะกรอกวันผิดไปทั้งใบโดยไม่มีอะไรเตือน ที่นี่จึงเป็นช่องข้อความที่เรา
// คุมเองทั้งการแสดงผลและปฏิทิน โดยยังคง "สัญญา" เดิมไว้ทุกอย่าง:
//
//   value     'yyyy-mm-dd' ค.ศ. (ค่า ISO จาก API ก็อ่านได้)
//   onChange  ได้ 'yyyy-mm-dd' ค.ศ. หรือ '' เมื่อล้างช่อง — ไม่ใช่ event
//   min/max   'yyyy-mm-dd' ค.ศ. เหมือน input[type=date]
//
// ที่ onChange คืนสตริงแทน event เพราะจุดเรียกทั้งหมดใช้แค่ e.target.value อยู่แล้ว
// การปลอม event ให้หน้าตาเหมือนเดิมมีแต่จะทำให้คนอ่านโค้ดเข้าใจผิดว่าเป็น input
// ของเบราว์เซอร์จริงๆ แล้วไปคาดหวัง target.validity / setCustomValidity ต่อ
//
// พ.ศ. อยู่แค่ชั้นที่คนอ่าน ค่าที่วิ่งใน state และที่ส่ง server ยังเป็น ค.ศ. เหมือนเดิม
// (ดูเหตุผลใน utils/thaiDate.js) — พิมพ์เองก็ได้ เลือกจากปฏิทินก็ได้ เพราะครูที่คีย์
// วันเกิดทั้งห้องไม่ควรต้องหยิบเมาส์ ส่วนคนที่จะเลือก "ศุกร์หน้า" ไม่ควรต้องนับวันเอง
import React, { forwardRef, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import DatePicker, { registerLocale } from 'react-datepicker'
import th from 'date-fns/locale/th'
import {
  THAI_MONTHS, THAI_WEEKDAYS_SHORT, toThaiDate, toISODate, toDateObject,
  isThaiDate, fromThaiDate, thaiDateInput,
} from '../utils/thaiDate'

registerLocale('th', th)

// ช่องข้อความของปฏิทิน — DatePicker จะ clone แล้วยัด props ของตัวเองมาทับ
// (value, onChange, className, placeholder, id, ...) ซึ่ง "ทับ" จริงๆ แม้ค่าเป็น
// undefined ของที่ต้องรอดจึงส่งผ่าน props ของ DatePicker ไม่ใช่ props ของแท็กนี้
//
// value/onChange ของมันถูกทิ้งทั้งคู่โดยตั้งใจ: value เป็น ค.ศ. ส่วน onChange จะเอา
// ข้อความที่พิมพ์ไป parse ตาม dateFormat ซึ่งอ่าน 2569 เป็น ค.ศ. 2569
// ข้อความในช่องเป็นของ ThaiDateInput ทั้งหมด ปฏิทินมีหน้าที่แค่เปิด/ปิดและเลือกวัน
const ThaiDateControl = forwardRef(function ThaiDateControl(
  { text, onText, onCommit, value, onChange, onBlur, ...rest }, ref,
) {
  return (
    <Form.Control
      {...rest}
      ref={ref}
      value={text}
      inputMode="numeric"
      maxLength={10}
      onChange={(e) => onText(thaiDateInput(e.target.value))}
      onBlur={(e) => { onCommit(); onBlur?.(e) }}
    />
  )
})

const ThaiDateInput = ({
  value,
  onChange,
  min,
  max,
  size,
  style,
  isInvalid,
  disabled,
  readOnly,
  required,
  className,
  id,
  name,
  title,
  placeholder = 'วว/ดด/ปปปป',
  yearRange,
  ...rest
}) => {
  const [text, setText] = useState(() => toThaiDate(value))

  // ค่าที่เปลี่ยนจากข้างนอก (โหลดข้อมูลมาเติมฟอร์ม / กดล้างตัวกรอง) ต้องสะท้อนในช่อง
  // ระหว่างพิมพ์ value ยังไม่เปลี่ยน effect นี้จึงไม่ไปทับสิ่งที่กำลังพิมพ์อยู่
  useEffect(() => { setText(toThaiDate(value)) }, [value])

  const handleText = (next) => {
    setText(next)
    if (!next.trim()) { onChange(''); return }
    // พอพิมพ์ครบและเป็นวันที่ที่มีอยู่จริงค่อยส่งออก ระหว่างที่ยังไม่ครบไม่ต้องรบกวนฟอร์ม
    if (isThaiDate(next)) onChange(fromThaiDate(next))
  }

  // ออกจากช่องแล้วข้อความยังไม่เป็นวันที่ = พิมพ์ค้างไว้ ให้กลับไปเป็นค่าจริงที่ฟอร์มถืออยู่
  // ปล่อยข้อความครึ่งๆ ค้างบนจอจะอ่านเหมือนกรอกแล้ว ทั้งที่ฟอร์มไม่ได้รับค่านั้น
  const handleCommit = () => {
    if (!text.trim()) { setText(''); onChange(''); return }
    if (isThaiDate(text)) { setText(toThaiDate(fromThaiDate(text))); return }
    setText(toThaiDate(value))
  }

  const thisYear = new Date().getFullYear()
  const [firstYear, lastYear] = yearRange || [
    toDateObject(min)?.getFullYear() ?? thisYear - 80,
    toDateObject(max)?.getFullYear() ?? thisYear + 10,
  ]
  const years = []
  for (let y = lastYear; y >= firstYear; y -= 1) years.push(y)

  return (
    <DatePicker
      selected={toDateObject(value)}
      onChange={(d) => onChange(d ? toISODate(d) : '')}
      minDate={toDateObject(min)}
      maxDate={toDateObject(max)}
      locale="th"
      disabled={disabled}
      readOnly={readOnly}
      {...(readOnly ? { open: false } : {})}
      required={required}
      className={className}
      id={id}
      name={name}
      title={title}
      placeholderText={placeholder}
      autoComplete="off"
      // ช่องวันที่ส่วนใหญ่อยู่ในโมดัลหรือในแถบตัวกรองที่เลื่อนได้ ปฏิทินที่วางแบบ
      // absolute จะถูกขอบกล่องตัดหัว — fixed ทำให้ลอยพ้นกรอบไปได้
      popperProps={{ strategy: 'fixed' }}
      popperClassName="thai-datepicker"
      showPopperArrow={false}
      todayButton="วันนี้"
      // ชื่อวันของ date-fns ภาษาไทยเป็นคำเต็ม ('อาทิตย์') หัวตารางกว้างแค่ตัวเดียว
      formatWeekDay={(dayName) => THAI_WEEKDAYS_SHORT[dayName] || dayName.slice(0, 2)}
      renderCustomHeader={({
        date, changeYear, changeMonth,
        decreaseMonth, increaseMonth,
        prevMonthButtonDisabled, nextMonthButtonDisabled,
      }) => (
        <div className="d-flex align-items-center justify-content-between gap-1 px-2 pb-2">
          <button type="button" className="btn btn-sm p-0 border-0 bg-transparent"
            onClick={decreaseMonth} disabled={prevMonthButtonDisabled} aria-label="เดือนก่อนหน้า">
            <i className="ri-arrow-left-s-line" />
          </button>
          <Form.Select size="sm" value={date.getMonth()} aria-label="เดือน"
            onChange={(e) => changeMonth(Number(e.target.value))}>
            {THAI_MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </Form.Select>
          {/* ปีในปฏิทินเป็น พ.ศ. ด้วย ไม่งั้นช่องกรอกกับปฏิทินจะบอกคนละปี
              ซึ่งสับสนกว่าการเป็น ค.ศ. ทั้งคู่เสียอีก */}
          <Form.Select size="sm" value={date.getFullYear()} aria-label="ปี พ.ศ."
            onChange={(e) => changeYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y + 543}</option>)}
          </Form.Select>
          <button type="button" className="btn btn-sm p-0 border-0 bg-transparent"
            onClick={increaseMonth} disabled={nextMonthButtonDisabled} aria-label="เดือนถัดไป">
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      )}
      customInput={(
        <ThaiDateControl
          text={text}
          onText={handleText}
          onCommit={handleCommit}
          size={size}
          style={style}
          isInvalid={isInvalid}
          {...rest}
        />
      )}
    />
  )
}

export default ThaiDateInput
